import { createContext, useContext, useEffect } from 'react';
import type { SiteSummary } from '../../lib/sites';

export type SidebarActions = {
  onUndo?: () => void;
  onToggleHistory?: () => void;
};

export type AppShellContextValue = {
  sites: SiteSummary[];
  sitesLoading: boolean;
  sitesError: string;
  refreshSites: () => Promise<void>;
  upsertSite: (site: SiteSummary) => void;
  removeSite: (siteId: string) => void;
  setSidebarActions: (actions: SidebarActions) => void;
  clearSidebarActions: () => void;
};

export const AppShellContext = createContext<AppShellContextValue | null>(null);

function useAppShellContext() {
  const context = useContext(AppShellContext);

  if (!context) {
    throw new Error('AppShell context is not available.');
  }

  return context;
}

export function useAppShellRefreshSites() {
  return useAppShellContext().refreshSites;
}

export function useAppShellSites() {
  const {
    sites,
    sitesLoading,
    sitesError,
    refreshSites,
    upsertSite,
    removeSite,
  } = useAppShellContext();

  return {
    sites,
    sitesLoading,
    sitesError,
    refreshSites,
    upsertSite,
    removeSite,
  };
}

export function useAppShellSidebarActions(actions: SidebarActions) {
  const { clearSidebarActions, setSidebarActions } = useAppShellContext();
  const { onUndo, onToggleHistory } = actions;

  useEffect(() => {
    if (!onUndo && !onToggleHistory) {
      clearSidebarActions();
      return undefined;
    }

    setSidebarActions({ onUndo, onToggleHistory });

    return () => {
      clearSidebarActions();
    };
  }, [clearSidebarActions, onToggleHistory, onUndo, setSidebarActions]);
}
